import { create } from "zustand"
import { retrieveCasa, retrieveDevices, retrieveLoan, retrieveSCA, retrieveTD, retrieveUsers, deleteUser, blockUser, unblockUser, removeDevice, updateDeviceStatus } from "./actions";
import { BankAccountInfo, ClientInfo, DeviceInfo, LoanInfo } from "./data";
import { MAX_USER_RETRIEVE } from "../../constants";


type UserStoreProps = {
    loading             :boolean
    users               :ClientInfo[] | null            // retrieved users
    currentUser         :ClientInfo | null              // current focus user
    retrieveUsers       : (query:string) => void        // function use to retrieve user
    retrieveUserQuery   : string                        // query use to retrieve users
    canLoadMoreUsers    : boolean
    setCurrentUser      : (user:ClientInfo) => void
    clearUsers          : () => void
    devices             :DeviceInfo[] | null
    retrieveDevices     :() => void
    removeDevice        :(deviceId: string) => Promise<void>
    updateDeviceStatus  :(deviceId: string, status: string) => Promise<void>

    // bank accounts
    currentUserCasa     : BankAccountInfo[] | null
    currentUserTD       : BankAccountInfo[] | null
    currentUserSCA      : BankAccountInfo[] | null
    currentUserLoan     : LoanInfo[] | null
    retrieveCasa        : () => void
    retrieveTD          : () => void
    retrieveSCA         : () => void
    retrieveLoan        : () => void
    error?   :string
    clearError         : () => void
    deleteUser          : (userId: string) => Promise<void>
    blockUser           : (clientId: string) => Promise<void>
    unblockUser         : (clientId: string) => Promise<void>
}

export const useUserStore = create<UserStoreProps>( (set,getState) => ({
    loading: false,
    users :null,
    canLoadMoreUsers: false,
    devices: null,
    currentUser: null,
    currentUserCasa: null,
    currentUserTD: null,
    currentUserLoan: null,
    currentUserSCA: null,
    retrieveUserQuery: "",
    setCurrentUser: (user:ClientInfo) => {
        set(state => ({currentUser: user}))
    },
    clearUsers() {
        set(state => ({currentUser: null, users:[]}))
    },
    clearError() {
        set(state => ({error: undefined}))
    },
    retrieveUsers :(query:string = "") => {
        if (getState().loading || getState().users?.length === 0) return

        set(state => ({loading:true, retrieveUserQuery: query}))

        let users = getState().users
        
        retrieveUsers(users === null ? 0: users.length, MAX_USER_RETRIEVE, query)
        .then( res => {
            const loadMore = res != null ? res?.length >= MAX_USER_RETRIEVE: false
            if (users === null) users = []
            //console.log(res)
            res?.forEach( (val) => users?.push(val))
            
            set(state => ({
                loading:false, 
                users:users, 
                canLoadMoreUsers: loadMore}))
        })
        .catch((err) => {
            const errorMessage = err.message || err.toString()
            set(state => ({loading:false, error:errorMessage, canLoadMoreUsers: false, users: state.users || []}))
        })
    },
    retrieveCasa() {
        const clientId = getState().currentUser?.ClientId
        //console.log(clientId, "asdfasdfs")
        retrieveCasa(clientId!)
        .then( res => {
            set(state => ({currentUserCasa: res}))
        })
        .catch((err) => {
            set(state => ({error:err}))
        })
    },
    retrieveTD() {
        const clientId = getState().currentUser?.ClientId
        //console.log(clientId, "asdfasdfs")
        retrieveTD(clientId!)
        .then( res => {
            set(state => ({currentUserTD: res}))
        })
        .catch((err) => {
            set(state => ({error:err}))
        })
    },
    retrieveLoan() {
        const clientId = getState().currentUser?.ClientId
        //console.log(clientId, "asdfasdfs")
        retrieveLoan(clientId!)
        .then( res => {
            set(state => ({currentUserLoan: res}))
        })
        .catch((err) => {
            set(state => ({error:err}))
        })
    },
    retrieveSCA() {
        const clientId = getState().currentUser?.ClientId
        //console.log(clientId, "asdfasdfs")
        retrieveSCA(clientId!)
        .then( res => {
            set(state => ({currentUserSCA: res}))
        })
        .catch((err) => {
            set(state => ({error:err}))
        })
    },
    retrieveDevices() {
        const clientId = getState().currentUser?.ClientId
        set(state => ({devices: []}))
        retrieveDevices(clientId!)
        .then( res => {
            set(state => ({devices: res}))
        })
        .catch((err) => {
            set(state => ({error:err}))
        })
    },
    async deleteUser(userId: string) {
        set(state => ({loading: true}))
        try {
            await deleteUser(userId)
            // Remove the deleted user from the users list
            const users = getState().users
            if (users) {
                const updatedUsers = users.filter(u => u.UserId !== userId)
                set(state => ({
                    users: updatedUsers,
                    loading: false,
                    currentUser: null
                }))
            }
        } catch (err: any) {
            set(state => ({loading: false, error: err.message || err.toString()}))
            throw err
        }
    },
    async blockUser(clientId: string) {
        set(state => ({loading: true}))
        try {
            await blockUser(clientId)
            // Update the user's status in the list
            const users = getState().users
            const currentUser = getState().currentUser
            if (users) {
                const updatedUsers = users.map(u =>
                    u.ClientId === clientId ? {...u, Status: "Blocked"} : u
                )
                set(state => ({
                    users: updatedUsers,
                    loading: false,
                    currentUser: currentUser && currentUser.ClientId === clientId
                        ? {...currentUser, Status: "Blocked"}
                        : currentUser
                }))
            } else {
                set(state => ({loading: false}))
            }
        } catch(err:any) {
            set(state => ({loading: false, error: err.message || err.toString()}))
            throw err
        }
    },
    async unblockUser(clientId: string) {
        set(state => ({loading: true}))
        try {
            await unblockUser(clientId)
            // Update the user's status in the list
            const users = getState().users
            const currentUser = getState().currentUser
            if (users) {
                const updatedUsers = users.map(u =>
                    u.ClientId === clientId ? {...u, Status: "Active"} : u
                )
                set(state => ({
                    users: updatedUsers,
                    loading: false,
                    currentUser: currentUser && currentUser.ClientId === clientId
                        ? {...currentUser, Status: "Active"} 
                        : currentUser
                }))
            }
        } catch (err: any) {
            set(state => ({loading: false, error: err.message || err.toString()}))
            throw err
        }
    },
    async removeDevice(deviceId: string) {
        const clientId = getState().currentUser?.ClientId
        if (!clientId) throw new Error("No client selected")

        try {
            await removeDevice(clientId, deviceId)
            // Remove the device from the local list
            const devices = getState().devices
            if (devices) {
                const updatedDevices = devices.filter(d => d.id !== deviceId)
                set(state => ({devices: updatedDevices}))
            }
        } catch (err: any) {
            set(state => ({error: err.message || err.toString()}))
            throw err
        }
    },
    async updateDeviceStatus(deviceId: string, status: string) {
        const clientId = getState().currentUser?.ClientId
        if (!clientId) throw new Error("No client selected")

        try {
            await updateDeviceStatus(clientId, deviceId, status)
            // Update the device status in the local list
            const devices = getState().devices
            if (devices) {
                const updatedDevices = devices.map(d =>
                    d.id === deviceId ? {...d, status: status as 'active' | 'blocked' | 'pending'} : d
                )
                set(state => ({devices: updatedDevices}))
            }
        } catch (err: any) {
            set(state => ({error: err.message || err.toString()}))
            throw err
        }
    },
}));